import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Bell, BellOff, Volume2, VolumeX, MonitorSmartphone } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';

const KEY = 'pillot_pesan_notif';
const load = () => { try { return { sound: true, browser: false, ...JSON.parse(localStorage.getItem(KEY) || '{}') }; } catch { return { sound: true, browser: false }; } };

let audioCtx = null;
export const playChime = () => {
  try {
    audioCtx = audioCtx || new (window.AudioContext || window.webkitAudioContext)();
    if (audioCtx.state === 'suspended') audioCtx.resume();
    const t = audioCtx.currentTime;
    [[880, 0], [1174.66, 0.12]].forEach(([f, d]) => {
      const o = audioCtx.createOscillator(); const g = audioCtx.createGain();
      o.type = 'sine'; o.frequency.value = f; o.connect(g); g.connect(audioCtx.destination);
      g.gain.setValueAtTime(0.0001, t + d); g.gain.exponentialRampToValueAtTime(0.18, t + d + 0.02); g.gain.exponentialRampToValueAtTime(0.0001, t + d + 0.35);
      o.start(t + d); o.stop(t + d + 0.4);
    });
  } catch { /* audio blocked */ }
};

/** Hook: preferensi per agen (localStorage) + fungsi notify(conv, message, isActive). */
export const useInboundNotifier = (onOpenConv) => {
  const [prefs, setPrefs] = useState(load);
  const [perm, setPerm] = useState(typeof Notification !== 'undefined' ? Notification.permission : 'unsupported');
  const prefsRef = useRef(prefs); prefsRef.current = prefs;
  const lastRef = useRef(0);
  useEffect(() => { localStorage.setItem(KEY, JSON.stringify(prefs)); }, [prefs]);
  const update = useCallback(async (patch) => {
    if (patch.browser && typeof Notification !== 'undefined' && Notification.permission !== 'granted') {
      const p = await Notification.requestPermission(); setPerm(p);
      if (p !== 'granted') { setPrefs((s) => ({ ...s, browser: false })); return; }
    }
    setPrefs((s) => ({ ...s, ...patch }));
  }, []);
  const notify = useCallback((conv, message, isActiveVisible) => {
    if (!message || message.direction !== 'in') return;
    const now = Date.now(); if (now - lastRef.current < 800) return; lastRef.current = now;
    const p = prefsRef.current;
    if (p.sound) playChime();
    if (p.browser && typeof Notification !== 'undefined' && Notification.permission === 'granted' && (document.hidden || !isActiveVisible)) {
      try {
        const name = conv?.contact?.name || conv?.visitor?.name || 'Pelanggan';
        const n = new Notification(`${name} · ${conv?.channel || 'pesan'}`, { body: (message.content || '[lampiran]').slice(0, 120), tag: `pesan-${conv?.id}`, renotify: true, icon: '/favicon.ico' });
        n.onclick = () => { window.focus(); onOpenConv?.(conv); n.close(); };
      } catch { /* ignore */ }
    }
  }, [onOpenConv]);
  return { prefs, update, notify, perm };
};

export const NotifMenu = ({ prefs, update, perm }) => {
  const any = prefs.sound || prefs.browser;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild><Button size="sm" variant="outline" className="rounded-xl h-8 px-2.5" title="Notifikasi pesan masuk" data-testid="notif-menu">{any ? <Bell size={14} /> : <BellOff size={14} className="text-muted-foreground" />}</Button></DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-72 rounded-xl p-3 space-y-3" data-testid="notif-panel">
        <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Notifikasi pesan masuk (perangkat ini)</div>
        <div className="flex items-center justify-between gap-2 text-sm"><span className="flex items-center gap-2">{prefs.sound ? <Volume2 size={14} /> : <VolumeX size={14} />}Bunyi saat pesan baru</span><Switch checked={prefs.sound} onCheckedChange={(v) => { update({ sound: v }); if (v) playChime(); }} data-testid="notif-sound" /></div>
        <div className="flex items-center justify-between gap-2 text-sm"><span className="flex items-center gap-2"><MonitorSmartphone size={14} />Notifikasi browser</span><Switch checked={prefs.browser} disabled={perm === 'unsupported' || perm === 'denied'} onCheckedChange={(v) => update({ browser: v })} data-testid="notif-browser" /></div>
        {perm === 'denied' && <p className="text-[11px] text-rose-600">Izin notifikasi diblokir browser. Izinkan lewat ikon gembok di address bar.</p>}
        {perm === 'unsupported' && <p className="text-[11px] text-muted-foreground">Browser ini tidak mendukung notifikasi.</p>}
        <p className="text-[11px] text-muted-foreground">Notifikasi browser muncul saat tab tidak aktif atau percakapan lain sedang dibuka.</p>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
